import { datadogRum } from '@datadog/browser-rum';
import isEqual from 'lodash/isEqual';
import React from 'react';

import { AdSdk, TransferApplicationState } from '@wetransfer/ad-sdk';
import {
  AdtechClient,
  AdtechProduct,
  AdSDKListeners,
} from '@wetransfer/adtech-common';

import { getPlanTier, userHasPaidTier } from '@wetransfer/transfer/helper/user';
import { usePrevious } from '@wetransfer/transfer/hooks/usePrevious';
import { useAdBlockDetection } from '@wetransfer/transfer/modules/ad-block-detection/hooks';
import { getEnv } from '@wetransfer/transfer/modules/ad-sdk-wallpaper/helpers';
import Analytics from '@wetransfer/transfer/modules/analytics';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import termsAndConditions from '@wetransfer/transfer/modules/terms-and-conditions';
import {
  connector,
  PropsFromRedux,
} from '@wetransfer/transfer/wallpaper-bundle/connector';

import { getGeo, getLanguage } from './helpers/override-helper';
import { getUserKey, hasUserConsentedAdvertisement } from './helpers/user';

export const MobileWebMicroFrontend = ({
  state,
}: PropsFromRedux): JSX.Element => {
  const rootNode = 'mobile-web-micro-frontend';

  const isAdvertisementEnabled = hasUserConsentedAdvertisement();

  const { isReady: isAdBlockReady, result: adBlockResult } =
    useAdBlockDetection();

  const { auth, session, user, route, layout } = state;

  const prevState = usePrevious(state);

  const language = getLanguage(session, route);
  const geo = getGeo(session, route);
  const key = getUserKey({ auth, session });
  const isTermsAccepted = termsAndConditions.areAccepted();
  const isPayingUser = userHasPaidTier(user);
  const demoAdvertId = route?.params?.advert_id;
  const userPlanTier = getPlanTier(user) ?? null;

  const getInternalState = React.useCallback(
    () => ({
      isTermsAccepted,
      isAdBlockEnabled: adBlockResult,
      isAdvertisementEnabled,
      client: AdtechClient.Transfer,
      product: AdtechProduct.WebMobile,
      geo,
      language,
      userAgent: Browser.identity,
      user: {
        key,
        isPayingUser,
        id: user?.id,
        auth0UserId: auth?.auth0UserId,
        planTier: userPlanTier,
      },
      isPanelOpen: route.isPanelOpen,
      isPanelHalfOpen: route.isPanelHalfOpen,
      isPanelFullOpen: route.isPanelFullOpen,
      dropzoneActive: layout.dropzoneActive,
      demoAdvertId,
      acceptedLanguages: (
        window['__session__'] as unknown as {
          user: {
            accepted_languages: string[];
          };
        }
      )?.user?.accepted_languages,
      transfer: {
        state: state.transfer.state,
      },
    }),
    [
      isTermsAccepted,
      adBlockResult,
      isAdvertisementEnabled,
      geo,
      language,
      key,
      isPayingUser,
      user?.id,
      auth?.auth0UserId,
      route.isPanelOpen,
      route.isPanelHalfOpen,
      route.isPanelFullOpen,
      layout.dropzoneActive,
      demoAdvertId,
      state.transfer.state,
      userPlanTier,
    ]
  );

  React.useEffect(() => {
    const handleUpdates = () => {
      if (prevState && !isEqual(prevState, getInternalState())) {
        try {
          window.dispatchEvent(
            new CustomEvent<Partial<TransferApplicationState>>(
              AdSDKListeners.AD_SDK_INTERNAL_STATE_UPDATE,
              {
                detail: getInternalState(),
              }
            )
          );
        } catch (error) {
          datadogRum.addError(
            new Error('ad_sdk_internal_state_update_failed'),
            { errorMessage: error.message }
          );
        }
      }
    };

    handleUpdates();
  }, [getInternalState, prevState]);

  React.useEffect(() => {
    if (!isAdBlockReady) {
      return;
    }

    const startAdSDK = async () => {
      await AdSdk.init({
        adUnitId: rootNode,
        adUnitProduct: AdtechProduct.WebMobile,
        adUnitClient: AdtechClient.Transfer,
        clientEnvironment: getEnv(),
        applicationState: getInternalState(),
        clientEventTracking: {
          trackUnstructSnowplowEvent: (schema: string, event: unknown) => {
            Analytics.trackUnstructSnowplowEvent(schema, event);
          },
        },
        errorTracking: {
          trackError: datadogRum.addError,
          addTiming: datadogRum.addTiming,
          addAction: datadogRum.addAction,
          addRumGlobalContext: datadogRum.addRumGlobalContext,
        },
      });
    };

    startAdSDK();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdBlockReady]);

  return (
    <div
      className="mobile-web-micro-frontend"
      id={rootNode}
      data-testid={rootNode}
    />
  );
};

export default connector(MobileWebMicroFrontend);
